import { AxiosInstance } from "axios"
import {
    CreateUpdateTemplate,
    GeneralSettings,
    Template,
    TemplateDetails,
    Theme,
    UpdateGeneralSettings,
} from "./supplierPosSettingsTypes"
import { ServiceResponse } from "../../../../../core/api/apiTypes"

export enum PosSettingQuery {
    TEMPLATES_LIST = "pos-templates-for-supplier",
    TEMPLATE_DETAILS = "details-for-template",
    GENERAL_SETTINGS = "pos-general-settings-for-supplier",
    THEMES_LIST = "themes-list",
}

type PosTemplatesForSupplierAdminParams = Readonly<{ supplierId: number }>

export type PosTemplatesForSupplierAdminResponse = Readonly<{
    templates: Template[]
}>

export async function fetchPosTemplatesForSupplier(
    { supplierId }: PosTemplatesForSupplierAdminParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<PosTemplatesForSupplierAdminResponse> {
    const { data } = await axios.get(`/suppliers/${supplierId}/settings/pos/templates`, {
        signal,
    })
    return data
}

type TemplateDetailsParams = Readonly<{
    templateId: number
}>

export type TemplateDetailsResponse = {
    template: TemplateDetails
}

export async function getTemplateDetails(
    params: TemplateDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<TemplateDetailsResponse> {
    const { templateId } = params
    const url = `/posTemplates/${templateId}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

type UpdateTemplateParams = {
    templateId: number
    template: CreateUpdateTemplate
}

export async function updateTemplate(
    params: UpdateTemplateParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { template, templateId } = params
    const response = await axios.post(
        `/posTemplates/${templateId}`,
        {
            template,
        },
        {
            signal,
        },
    )
    return response.data
}

type CreateTemplateParams = { template: CreateUpdateTemplate; supplierId: number }

export async function createTemplate(
    params: CreateTemplateParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { template, supplierId } = params

    const { data } = await axios.post(
        `/posTemplates`,
        {
            template: {
                ...template,
                supplier: {
                    id: supplierId,
                },
            },
        },
        {
            signal,
        },
    )
    return data
}

type PosThemesParams = Readonly<{
    supplierId: number
}>

export type PosThemesResponse = {
    themes: Theme[]
}

export async function getPosThemes(
    params: PosThemesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<PosThemesResponse> {
    const { supplierId } = params
    const url = `/suppliers/${supplierId}/settings/pos/themes`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

// GENERAL
type GeneralSettingsParams = Readonly<{
    supplierId: number
}>

export type GeneralSettingsResponse = {
    generalSettings: GeneralSettings
}

export async function getGeneralSettings(
    params: GeneralSettingsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<GeneralSettingsResponse> {
    const { supplierId } = params
    const url = `/suppliers/${supplierId}/settings/pos/general`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

type UpdateGeneralSettingsParams = {
    supplierId: number
    generalSettings: UpdateGeneralSettings
}

export async function updateGeneralSettings(
    params: UpdateGeneralSettingsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { generalSettings, supplierId } = params
    const response = await axios.post(
        `/suppliers/${supplierId}/settings/pos/general`,
        {
            generalSettings,
        },
        {
            signal,
        },
    )
    return response.data
}
